/* ==========================================================
   JI FIRE - AUTO AVAILABLE VARIANT V7 FINAL
   For Shopify <variant-selects> + product-variant-options
   ==========================================================

   IMPORTANT:
   1) Remove/disable V5 and V6. Keep ONLY this engine.
   2) window.JIFIRE_PRODUCT_VARIANTS must contain product variants.
   3) This theme marks unavailable radios with class="disabled";
      it does NOT necessarily use the HTML disabled attribute.

   RESULT:
   - Initial page: moves to first available real variant if current is sold out.
   - Change storage/SIM/etc.: automatically chooses FIRST available later
     option/color for that prefix.
   - Manual click on a sold-out LAST option remains possible, allowing the
     theme's native back-in-stock UI to appear.
   - Re-checks AFTER the theme updates its availability classes/DOM.
*/

(function () {
  'use strict';

  if (window.__JIFIRE_VARIANT_ENGINE_V7__) return;
  window.__JIFIRE_VARIANT_ENGINE_V7__ = true;

  const CFG = {
    initialDelay: 450,
    afterThemeDelay: 80,
    secondPassDelay: 220,
    retries: 12,
    retryDelay: 250
  };

  let busy = false;
  let correctionTimer = null;
  let secondTimer = null;

  const norm = v => String(v == null ? '' : v).trim().toLowerCase();
  const same = (a, b) => norm(a) === norm(b);

  function getPicker() {
    return (
      document.querySelector('product-info variant-selects') ||
      document.querySelector('variant-selects')
    );
  }

  function getGroups(picker) {
    if (!picker) return [];
    return Array.from(
      picker.querySelectorAll('fieldset.product-form__input')
    ).filter(g => g.querySelector('input[type="radio"]'));
  }

  function getVariants() {
    const v = window.JIFIRE_PRODUCT_VARIANTS;
    return Array.isArray(v) ? v : [];
  }

  function inputs(group) {
    return Array.from(group.querySelectorAll('input[type="radio"]'));
  }

  function selectedValue(group) {
    const x = group.querySelector('input[type="radio"]:checked');
    return x ? x.value : null;
  }

  function selectedValues(groups) {
    return groups.map(selectedValue);
  }

  function isRealVariant(v, count) {
    return !!(v && Array.isArray(v.options) && v.options.length === count);
  }

  function exactVariant(vs, vals) {
    return vs.find(v =>
      isRealVariant(v, vals.length) &&
      v.options.every((x, i) => same(x, vals[i]))
    ) || null;
  }

  function inputByValue(group, value) {
    return inputs(group).find(x => same(x.value, value)) || null;
  }

  /*
    Shopify's variant array order is retained. Therefore this returns the
    FIRST available real variant matching the prefix selected by the user.
  */
  function firstAvailableWithPrefix(vs, vals, throughIndex) {
    return vs.find(v => {
      if (!isRealVariant(v, vals.length) || v.available !== true) return false;

      for (let i = 0; i <= throughIndex; i++) {
        if (vals[i] != null && !same(v.options[i], vals[i])) return false;
      }
      return true;
    }) || null;
  }

  function firstAvailable(vs, count) {
    return vs.find(v => isRealVariant(v, count) && v.available === true) || null;
  }

  /*
    Set checked states in a batch, then emit only ONE change.
    No click simulation and no permanent disabled/class manipulation.
  */
  function applyTarget(groups, target, startIndex) {
    let changed = null;

    for (let i = Math.max(0, startIndex); i < groups.length; i++) {
      const radio = inputByValue(groups[i], target.options[i]);
      if (!radio) return null;

      if (!same(selectedValue(groups[i]), target.options[i])) {
        radio.checked = true;
        changed = radio;
      }
    }

    return changed;
  }

  function emitChange(input) {
    if (!input) return;

    busy = true;
    try {
      input.dispatchEvent(new Event('change', {
        bubbles: true
      }));
    } finally {
      // Give synchronous theme listeners the event first.
      setTimeout(() => { busy = false; }, 0);
    }
  }

  /*
    Core correction AFTER the theme has processed the user's change.

    changedIndex is the option the customer deliberately changed.
    We preserve options 0..changedIndex and only repair later options.
  */
  function correctAfterTheme(changedIndex) {
    if (busy) return;

    const picker = getPicker();
    const groups = getGroups(picker);
    const vs = getVariants();

    if (!picker || !groups.length || !vs.length) return;

    const vals = selectedValues(groups);
    if (vals.some(v => v == null)) return;

    const current = exactVariant(vs, vals);

    // Already valid and available.
    if (current && current.available === true) return;

    /*
      If customer explicitly selected the final option and that exact real
      variant is sold out, do NOT move them away. This keeps "notify me"
      behavior available.
    */
    if (
      changedIndex === groups.length - 1 &&
      current &&
      current.available === false
    ) {
      return;
    }

    const target = firstAvailableWithPrefix(vs, vals, changedIndex);
    if (!target) return;

    const changed = applyTarget(groups, target, changedIndex + 1);
    if (changed) emitChange(changed);
  }

  function scheduleCorrection(changedIndex) {
    clearTimeout(correctionTimer);
    clearTimeout(secondTimer);

    correctionTimer = setTimeout(
      () => correctAfterTheme(changedIndex),
      CFG.afterThemeDelay
    );

    /*
      Second quiet pass is intentional for WebViews/themes that update
      availability classes slightly later.
    */
    secondTimer = setTimeout(
      () => correctAfterTheme(changedIndex),
      CFG.secondPassDelay
    );
  }

  function onUserChange(event) {
    if (busy) return;

    const picker = getPicker();
    if (!picker || !picker.contains(event.target)) return;

    const groups = getGroups(picker);
    const changedIndex = groups.findIndex(g => g.contains(event.target));

    if (changedIndex < 0) return;

    /*
      Let the theme fully process the original event first.
      Then inspect the REAL resulting variant and correct only if necessary.
    */
    scheduleCorrection(changedIndex);
  }

  function initialSync(attempt) {
    attempt = Number.isInteger(attempt) ? attempt : 0;

    const picker = getPicker();
    const groups = getGroups(picker);
    const vs = getVariants();

    if (!picker || !groups.length || !vs.length) {
      if (attempt < CFG.retries) {
        setTimeout(() => initialSync(attempt + 1), CFG.retryDelay);
      }
      return;
    }

    const vals = selectedValues(groups);

    if (vals.some(v => v == null)) {
      if (attempt < CFG.retries) {
        setTimeout(() => initialSync(attempt + 1), CFG.retryDelay);
      }
      return;
    }

    const current = exactVariant(vs, vals);
    if (current && current.available === true) return;

    const target = firstAvailable(vs, groups.length);
    if (!target) return;

    const changed = applyTarget(groups, target, 0);
    if (changed) emitChange(changed);
  }

  function init() {
    setTimeout(() => initialSync(0), CFG.initialDelay);
  }

  document.addEventListener('change', onUserChange, false);

  document.addEventListener('shopify:section:load', function () {
    setTimeout(() => initialSync(0), CFG.initialDelay);
  });

  window.addEventListener('pageshow', function () {
    setTimeout(() => initialSync(0), CFG.initialDelay);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
